///
/// EarthBrowser
///
/// Created by Matt Giger on 10/31/2026
/// Copyright (c) 2026 Geodata Labs
///
/// One instanced draw for the planet-view density glow (webcam_glow.glsl):
/// a soft ground disc per regional cluster. Same frame protocol as the
/// other webcam batches; draws first in the transparent pass so chips and
/// badges sit on top of it.
///

import { NodeBase } from "@stovokor/engine/NodeBase"
import { type RenderActionSet, Renderer } from "@stovokor/engine/Renderer"
import { type RenderState } from "@stovokor/engine/RenderState"
import { cube } from "@stovokor/math/cube"
import { type matrix } from "@stovokor/math/matrix"
import { sphere } from "@stovokor/math/sphere"
import { type time } from "@stovokor/math/time"
import { vec3 } from "@stovokor/math/vector"
import { type Geometry } from "@stovokor/shader/Geometry"
import { DrawMode } from "@stovokor/shader/GLConstants"
import { IndexBuffer } from "@stovokor/shader/IndexBuffer"
import { type Material } from "@stovokor/shader/Material"
import { ShaderSourceDefinition } from "@stovokor/shader/ShaderSourceCode"
import { UniformDynamic } from "@stovokor/shader/Uniform"
import { VertexAttribute } from "@stovokor/shader/VertexAttribute"
import { VertexBuffer } from "@stovokor/shader/VertexBuffer"
import { type StovokorContext } from "@stovokor/StovokorContext"

// center.xyz, normal.xyz, radius km, opacity
const cFloatsPerInstance = 8
const cInitialCapacity = 256
const cBoundsKm = 1e7
// fuchsia-500 warmed toward pink
const cGlowColor = new vec3(0.9, 0.3, 0.85)

class WebcamGlowGeometry implements Geometry {
	public corner: VertexAttribute
	public instanceCenter: VertexAttribute
	public instanceNormal: VertexAttribute
	public instanceParams: VertexAttribute
	public vertexBuffer: VertexBuffer
	public instanceBuffer: VertexBuffer
	public indexBuffer: IndexBuffer
	public mode: DrawMode = DrawMode.Triangles
	public bounds = new cube(-cBoundsKm, cBoundsKm)
	public boundingSphere = new sphere(cBoundsKm)
	public available: boolean = true
	public instanceCount: number = 0

	constructor(context: StovokorContext) {
		this.corner = new VertexAttribute(context, 2, [-1, -1, 1, -1, 1, 1, -1, 1])
		this.vertexBuffer = new VertexBuffer(context, [this.corner])
		this.indexBuffer = new IndexBuffer(context)
		this.indexBuffer.quad(0, 1, 2, 3)

		this.instanceCenter = new VertexAttribute(context, 3)
		this.instanceNormal = new VertexAttribute(context, 3)
		this.instanceParams = new VertexAttribute(context, 2)
		const instanced = [this.instanceCenter, this.instanceNormal, this.instanceParams]
		for (const attribute of instanced) {
			attribute.divisor = 1
		}
		this.instanceBuffer = new VertexBuffer(context, instanced)
		this.instanceBuffer.usage = WebGL2RenderingContext.DYNAMIC_DRAW
	}

	public destroy(): void {
		this.vertexBuffer.destroy()
		this.instanceBuffer.destroy()
		this.indexBuffer.destroy()
	}
}

class WebcamGlowRenderer extends Renderer {
	public modelView = new UniformDynamic<matrix>()
	public projection = new UniformDynamic<matrix>()
	public glowColor = new UniformDynamic<vec3>(cGlowColor)

	constructor(context: StovokorContext, geometry: Geometry, finalize: () => void) {
		const transparentMaterial: Material = { transparency: true, available: true }
		super(context, new ShaderSourceDefinition("webcam_glow"), geometry, [transparentMaterial])
		this.drawState.cullFace = false
		this.preActions = [finalize]
	}

	protected stateUpdate(state: RenderState): void {
		this.modelView.value = state.modelView
		this.projection.value = state.projection
	}
}

export class WebcamGlowBatch extends NodeBase {
	private readonly geometry: WebcamGlowGeometry
	private data = new Float32Array(cFloatsPerInstance * cInitialCapacity)
	private count = 0
	private frame?: time

	constructor(context: StovokorContext) {
		super()
		// Under the markers of the same pass
		this.renderOrder = -1
		this.geometry = new WebcamGlowGeometry(context)
		this.renderer = new WebcamGlowRenderer(context, this.geometry, () => this.finalize())
	}

	/** Discs appended for the frame being drawn (debugging) */
	public get instanceCount(): number {
		return this.count
	}

	/** Append one disc at world point (x, y, z) km with surface normal (nx, ny, nz) */
	public add(state: RenderState, x: number, y: number, z: number, nx: number, ny: number, nz: number, radiusKm: number, opacity: number): void {
		this.beginFrame(state)
		if ((this.count + 1) * cFloatsPerInstance > this.data.length) {
			const grown = new Float32Array(this.data.length * 2)
			grown.set(this.data)
			this.data = grown
		}
		const d = this.data
		let i = this.count * cFloatsPerInstance
		d[i++] = x
		d[i++] = y
		d[i++] = z
		d[i++] = nx
		d[i++] = ny
		d[i++] = nz
		d[i++] = radiusKm
		d[i] = opacity
		this.count += 1
	}

	public override render(state: RenderState): RenderActionSet[] {
		this.beginFrame(state)
		return super.render(state)
	}

	private beginFrame(state: RenderState): void {
		if (this.frame !== state.realTime) {
			this.frame = state.realTime
			this.count = 0
		}
	}

	private finalize(): void {
		this.geometry.instanceCount = this.count
		if (this.count > 0) {
			this.geometry.instanceBuffer.uploadData(this.data)
		}
	}
}

// One batch per GL context
const sharedBatches = new WeakMap<StovokorContext, WebcamGlowBatch>()

export function sharedWebcamGlowBatch(context: StovokorContext): WebcamGlowBatch {
	let batch = sharedBatches.get(context)
	if (!batch) {
		batch = new WebcamGlowBatch(context)
		sharedBatches.set(context, batch)
	}
	return batch
}
